import { BarChart3, Building2, Edit, Filter, MapPin, Plus, Search, Settings, Trash2, TrendingUp, Users, Wrench } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const Buildings = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [showStatistics, setShowStatistics] = useState(false);
  const [selectedPropertyOwner, setSelectedPropertyOwner] = useState('');
  const [selectedBuildingFilter, setSelectedBuildingFilter] = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  // 현재 사용자 정보 가져오기
  const { data: userInfo } = useQuery('user-info', async () => {
    const response = await api.get('/auth/me');
    setCurrentUser(response);
    return response;
  });

  const isRMCUser = ['rental_management_company', 'rmc_admin', 'rmc_staff'].includes(userInfo?.role);

  // 임대사업자 목록 가져오기 (RMC 사용자용)
  const { data: propertyOwners } = useQuery(
    ['property-owners', userInfo?.role],
    () => api.get('/property-owners'),
    {
      enabled: isRMCUser,
      staleTime: 5 * 60 * 1000, // 5분
      cacheTime: 10 * 60 * 1000, // 10분
    }
  );

  useEffect(() => {
    setSelectedBuildingFilter('');
  }, [selectedPropertyOwner]);

  // 건물 목록 가져오기
  const { data: buildings, isLoading } = useQuery(['buildings', selectedPropertyOwner], async () => {
    let url = '/buildings';
    if (selectedPropertyOwner) {
      url += `?property_owner_id=${selectedPropertyOwner}`;
    }
    const response = await api.get(url);
    return Array.isArray(response) ? response : response?.data || [];
  });

  // 선택된 건물의 통계 정보 가져오기
  const { data: buildingStats } = useQuery(
    ['building-stats', selectedBuilding?.id],
    () => api.get(`/buildings/${selectedBuilding.id}/statistics`),
    { enabled: !!selectedBuilding && showStatistics }
  );

  // 건물 삭제 뮤테이션
  const deleteBuildingMutation = useMutation(
    (buildingId) => api.delete(`/buildings/${buildingId}`),
    {
      onSuccess: () => {
        toast.success('건물이 성공적으로 삭제되었습니다.');
        queryClient.invalidateQueries('buildings');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '건물 삭제에 실패했습니다.');
      }
    }
  );

  // 건물 삭제 확인
  const handleDeleteBuilding = (building) => {
    if (window.confirm(`"${building.name}" 건물을 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다.`)) {
      deleteBuildingMutation.mutate(building.id);
    }
  };

  // 디버깅 로그 추가
  console.log('🔍 Buildings 컴포넌트 디버깅:');
  console.log('  - buildings 타입:', typeof buildings);
  console.log('  - buildings 값:', buildings);
  console.log('  - buildings가 배열인가?', Array.isArray(buildings));
  console.log('  - buildings 길이:', buildings?.length);

  // 검색 및 필터링
  const filteredBuildings = buildings?.filter(building => {
    const matchesSearch = building.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      building.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || building.status === filterStatus;
    const matchesBuilding = !selectedBuildingFilter || String(building.id) === String(selectedBuildingFilter);
    return matchesSearch && matchesFilter && matchesBuilding;
  }) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 페이지 제목 */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">건물 관리</h1>
          <p className="mt-1 text-sm text-gray-500">
            등록된 건물들을 관리하고 모니터링하세요
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setShowStatistics(!showStatistics)}
            className="inline-flex items-center whitespace-nowrap px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <BarChart3 className="mr-2 h-4 w-4 flex-shrink-0" />
            통계 보기
          </button>
          <button
            onClick={() => navigate('/buildings/add')}
            className="inline-flex items-center whitespace-nowrap px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Plus className="mr-2 h-4 w-4 flex-shrink-0" />
            건물 추가
          </button>
        </div>
      </div>

      {/* 전체 통계 카드 */}
      {showStatistics && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Building2 className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">총 건물 수</dt>
                    <dd className="text-lg font-medium text-gray-900">{buildings?.length || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">총 세대 수</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {buildings?.reduce((total, building) => total + (building.total_units || 0), 0) || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrendingUp className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">평균 입주율</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {buildings?.length > 0
                        ? Math.round(buildings.reduce((total, building) => {
                          const rate = building.total_units > 0
                            ? (building.occupied_units / building.total_units) * 100
                            : 0;
                          return total + rate;
                        }, 0) / buildings.length)
                        : 0}%
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Wrench className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">유지보수 건물</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {buildings?.filter(building => building.status === 'maintenance').length || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 검색 및 필터 */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
          {isRMCUser && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">임대사업자</label>
              <select
                value={selectedPropertyOwner}
                onChange={(e) => setSelectedPropertyOwner(e.target.value)}
                className="block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="">전체 임대사업자</option>
                {propertyOwners?.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.company_name || owner.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">건물</label>
            <select
              value={selectedBuildingFilter}
              onChange={(e) => setSelectedBuildingFilter(e.target.value)}
              disabled={isRMCUser && !selectedPropertyOwner}
              className="block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-gray-100"
            >
              <option value="">{isRMCUser && !selectedPropertyOwner ? '임대사업자 먼저 선택' : '전체 건물'}</option>
              {buildings?.map((building) => (
                <option key={building.id} value={building.id}>
                  {building.name}
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <label className="block text-xs font-medium text-gray-500 mb-1">검색</label>
            <div className="absolute left-0 bottom-0 pl-3 h-10 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="건물명 또는 주소"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>

          <div className="relative">
            <label className="block text-xs font-medium text-gray-500 mb-1">상태</label>
            <div className="absolute left-0 bottom-0 pl-3 h-10 flex items-center pointer-events-none">
              <Filter className="h-5 w-5 text-gray-400" />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-10 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="all">전체 상태</option>
              <option value="active">활성</option>
              <option value="inactive">비활성</option>
              <option value="maintenance">유지보수</option>
            </select>
          </div>

          <div className="flex items-end justify-end">
            <p className="text-sm text-gray-500">총 {filteredBuildings.length}개 건물</p>
          </div>
        </div>
      </div>

      {/* 건물 목록 */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {filteredBuildings.map((building) => (
          <div key={building.id} className="bg-white shadow rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
            {/* 건물 이미지 */}
            <div className="h-48 bg-gray-200 flex items-center justify-center relative">
              <Building2 className="h-16 w-16 text-gray-400" />
              <div className="absolute top-2 right-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${building.status === 'active' ? 'bg-green-100 text-green-800' :
                  building.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                  {building.status === 'active' ? '활성' :
                    building.status === 'inactive' ? '비활성' : '유지보수'}
                </span>
              </div>
            </div>

            {/* 건물 정보 */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{building.name}</h3>
              </div>

              <div className="space-y-2">
                <div className="flex items-center text-sm text-gray-500">
                  <MapPin className="mr-2 h-4 w-4" />
                  {building.address}
                </div>
                <div className="flex items-center text-sm text-gray-500">
                  <Users className="mr-2 h-4 w-4" />
                  {building.total_units}개 호수 • {building.occupied_units}개 임대중
                </div>
                {building.building_type && (
                  <div className="flex items-center text-sm text-gray-500">
                    <Building2 className="mr-2 h-4 w-4" />
                    {building.building_type}
                  </div>
                )}
              </div>

              {/* 임대율 */}
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">임대율</span>
                  <span className="font-medium text-gray-900">
                    {building.total_units > 0
                      ? Math.round((building.occupied_units / building.total_units) * 100)
                      : 0}%
                  </span>
                </div>
                <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${building.total_units > 0
                        ? (building.occupied_units / building.total_units) * 100
                        : 0}%`
                    }}
                  ></div>
                </div>
              </div>

              {/* 액션 버튼 */}
              <div className="mt-4 space-y-2">
                {/* 주요 버튼 */}
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setSelectedBuilding(building);
                      setShowStatistics(true);
                    }}
                    className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <BarChart3 className="mr-1 h-4 w-4" />
                    통계
                  </button>
                  <button
                    onClick={() => navigate(`/buildings/${building.id}/units`)}
                    className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <Settings className="mr-1 h-4 w-4" />
                    세대관리
                  </button>
                </div>

                {/* 수정/삭제 버튼 */}
                <div className="flex space-x-2">
                  <button
                    onClick={() => navigate(`/buildings/${building.id}/edit`)}
                    className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <Edit className="mr-1 h-4 w-4" />
                    수정
                  </button>
                  <button
                    onClick={() => handleDeleteBuilding(building)}
                    disabled={deleteBuildingMutation.isLoading}
                    className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="mr-1 h-4 w-4" />
                    {deleteBuildingMutation.isLoading ? '삭제 중...' : '삭제'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 선택된 건물의 상세 통계 */}
      {selectedBuilding && showStatistics && buildingStats && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {selectedBuilding.name} - 상세 통계
            </h2>
            <button
              onClick={() => setSelectedBuilding(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500 mb-2">세대 현황</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">총 세대</span>
                  <span className="font-medium">{buildingStats.unit_statistics.total_units}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">입주 세대</span>
                  <span className="font-medium text-green-600">{buildingStats.unit_statistics.occupied_units}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">빈 세대</span>
                  <span className="font-medium text-blue-600">{buildingStats.unit_statistics.available_units}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">입주율</span>
                  <span className="font-medium">{buildingStats.unit_statistics.occupancy_rate}%</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500 mb-2">시설 현황</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">총 시설</span>
                  <span className="font-medium">{buildingStats.facility_statistics.total_facilities}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">정상 운영</span>
                  <span className="font-medium text-green-600">{buildingStats.facility_statistics.operational_facilities}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">정비 중</span>
                  <span className="font-medium text-yellow-600">{buildingStats.facility_statistics.maintenance_facilities}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">운영율</span>
                  <span className="font-medium">{buildingStats.facility_statistics.operational_rate}%</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500 mb-2">점검 현황</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">총 점검</span>
                  <span className="font-medium">{buildingStats.inspection_statistics.total_inspections}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">이번 달</span>
                  <span className="font-medium text-blue-600">{buildingStats.inspection_statistics.recent_inspections}</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500 mb-2">수익 현황</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">월세 수익</span>
                  <span className="font-medium">{buildingStats.revenue_statistics.total_monthly_rent?.toLocaleString()}원</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">관리비 수익</span>
                  <span className="font-medium">{buildingStats.revenue_statistics.total_maintenance_fee?.toLocaleString()}원</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-sm font-medium text-gray-600">총 수익</span>
                  <span className="font-bold text-green-600">{buildingStats.revenue_statistics.total_monthly_revenue?.toLocaleString()}원</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 빈 상태 */}
      {filteredBuildings.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">건물이 없습니다</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || filterStatus !== 'all'
              ? '검색 조건에 맞는 건물이 없습니다.'
              : '첫 번째 건물을 추가해보세요.'}
          </p>
          {!searchTerm && filterStatus === 'all' && (
            <div className="mt-6">
              <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                <Plus className="mr-2 h-4 w-4" />
                건물 추가
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Buildings;
